import { toast } from 'react-toastify';

/**
 * Toast Manager to prevent duplicate toasts
 */
class ToastManager {
  constructor() {
    this.activeToasts = new Set();
    this.toastTimeouts = new Map();
  }

  /**
   * Show a toast only if it's not already active
   * @param {string} type - Toast type (success, error, info, warning)
   * @param {string} message - Toast message
   * @param {Object} options - Toast options
   */
  show(type, message, options = {}) {
    const toastKey = `${type}:${message}`;
    
    // If this exact toast is already active, don't show it again
    if (this.activeToasts.has(toastKey)) {
      return null;
    }

    // Clear any existing timeout for this toast
    if (this.toastTimeouts.has(toastKey)) {
      clearTimeout(this.toastTimeouts.get(toastKey));
    }

    // Mark this toast as active
    this.activeToasts.add(toastKey);

    // Show the toast
    const toastId = toast[type](message, {
      ...options,
      onClose: () => {
        this.activeToasts.delete(toastKey);
        this.toastTimeouts.delete(toastKey);
        if (options.onClose) {
          options.onClose();
        }
      }
    });

    // Set a timeout to remove from active set (fallback in case onClose doesn't fire)
    const timeout = setTimeout(() => {
      this.activeToasts.delete(toastKey);
      this.toastTimeouts.delete(toastKey);
    }, options.autoClose || 5000);

    this.toastTimeouts.set(toastKey, timeout);

    return toastId;
  }

  /**
   * Show success toast (deduplicated)
   */
  success(message, options = {}) {
    return this.show('success', message, options);
  }

  /**
   * Show error toast (deduplicated)
   */
  error(message, options = {}) {
    return this.show('error', message, options);
  }

  /**
   * Show info toast (deduplicated)
   */
  info(message, options = {}) {
    return this.show('info', message, options);
  }

  /**
   * Show warning toast (deduplicated)
   */
  warning(message, options = {}) {
    return this.show('warning', message, options);
  }

  /**
   * Dismiss all toasts
   */
  dismissAll() {
    toast.dismiss();
    this.activeToasts.clear();
    
    // Clear all timeouts
    for (const timeout of this.toastTimeouts.values()) {
      clearTimeout(timeout);
    }
    this.toastTimeouts.clear();
  }

  /**
   * Dismiss specific toast by message
   */
  dismiss(message) {
    const keysToRemove = [];
    for (const key of this.activeToasts) {
      if (key.includes(message)) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => {
      this.activeToasts.delete(key);
      if (this.toastTimeouts.has(key)) {
        clearTimeout(this.toastTimeouts.get(key));
        this.toastTimeouts.delete(key);
      }
    });
    
    toast.dismiss();
  }

  /**
   * Check if a toast is currently active
   */
  isActive(type, message) {
    return this.activeToasts.has(`${type}:${message}`);
  }
}

// Create singleton instance
export const toastManager = new ToastManager();

// Export convenience methods
export const showToast = {
  success: (message, options) => toastManager.success(message, options),
  error: (message, options) => toastManager.error(message, options),
  info: (message, options) => toastManager.info(message, options),
  warning: (message, options) => toastManager.warning(message, options),
  dismiss: (message) => toastManager.dismiss(message),
  dismissAll: () => toastManager.dismissAll(),
};

export default toastManager;